/* eslint-disable complexity */
import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import {
	Box,
	Button,
	ButtonBase,
	Collapse,
	Divider,
	Grid,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import React, { useState } from 'react';
import StarsIcon from '@mui/icons-material/Stars';
import { ConfirmCredentialsDialog } from '@/components/blocks/ConfirmCredentialsDialog';
import { ACCOUNT_TABS } from '@/components/content/Account/parts/Tabs';
import { useNotifications } from '@/data/Content/Notifications';
import {
	ButtonRewardsSX,
	ClubDpRewardDescriptionSX,
	ClubDpRewardsActionSX,
	ClubDpRewardsCardDateSX,
	ClubDpRewardsCardFrameSX,
	ClubDpRewardsCardHolderFrameSX,
	ClubDpRewardsCardHolderSX,
	ClubDpRewardsCardImgSX,
	ClubDpRewardsCardLabelBlackSX,
	ClubDpRewardsCardLabelSX,
	ClubDpRewardsCardNameSX,
	ClubDpRewardsCardNumberSX,
	ClubDpRewardsCardPointsFrameSX,
	ClubDpRewardsCardPointsIconSX,
	ClubDpRewardsCardPointsSX,
	ClubDpRewardsCardSinceSX,
	ClubDpRewardsCollapseSX,
	ClubDpRewardsDividerSX,
	ClubDpRewardsTicketsTitleSX,
	ClubDpRewardsTitleSX,
} from '@/components/content/Account/styles/clubDpRewards';
import CustomProgressBar from '@/components/content/Account/parts/ClubDpRewards/CustomProgressBar';
import { DpRewardsCrown } from '@/components/content/Account/parts/DpRewardsCrown';
import { DpRewardsTrophy } from '@/components/content/Account/parts/DpRewardsTrophy';
import { LatestMovementsTable } from '@/components/content/Account/parts/ClubDpRewards/LatestMovementsTable';
import { PeriodsModal } from '@/components/content/Account/parts/ClubDpRewards/PeriodsModal';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { MainCardViewSkeleton } from '@/components/content/Account/parts/Skeletons/MainCardViewSkeleton';
import { combineSX } from '@/utils/combineSX';

interface MainCardViewProps {
	cards: any;
	loading: any;
	onTabSelect?: (tab: ACCOUNT_TABS) => void;
	onMutateClubDpFlags?: () => void;
	cardUnlink: any;
}

const formatCardId = (id: string) => id.match(/.{1,4}/g)?.join(' ');

export const MainCardView: React.FC<MainCardViewProps> = ({
	cards,
	loading,
	cardUnlink,
	onTabSelect,
	onMutateClubDpFlags,
}: any) => {
	const isMobile = useIsMobile();
	const [openConfirm, setOpenConfirm] = React.useState(false);
	const [openModalPeriods, setOpenModalPeriods] = React.useState(false);
	const [imageLoaded, setImageLoaded] = useState<boolean>(false);
	const { showErrorMessage } = useNotifications();
	const {
		customerName,
		activate,
		balancePoints,
		cardId,
		level,
		pointsForNextLevel,
		latestMovements,
		periods,
	} = cards?.rewards;

	const emsNameRewards = `DPSV2_BenefitCoupon_${level}`;
	const stripRewards = `DPSV2_StripBenefit_${level}_DPRewards`;
	const nextRewards = `DPSV2_NextBenefit_${level}_DPRewards`;

	const date = new Date(activate);
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();
	const formattedDate = `${month}/${year}`;

	const getCardImage = () => {
		switch (level) {
			case 'ELITE':
				return '/images/Tarjeta-elite-frente.png';
			case 'VIP':
				return '/images/Tarjeta-VIP-frente.png';
			case 'FAN':
			default:
				return '/images/Tarjeta-fan-frente.png';
		}
	};

	const getLevelMessage = () => {
		const getIcon = () => {
			switch (level) {
				case 'ELITE':
				case 'VIP':
					return <DpRewardsTrophy />;
				case 'FAN':
				default:
					return <DpRewardsCrown />;
			}
		};

		const getMessage = () => {
			switch (level) {
				case 'ELITE':
					return `Te faltan ${pointsForNextLevel} puntos para ser nivel `;
				case 'VIP':
					return 'No olvides acumular puntos para mantener el ';
				case 'FAN':
				default:
					return `Te faltan ${pointsForNextLevel} puntos para ser nivel `;
			}
		};

		return (
			<Typography variant="caption">
				<>
					{getMessage()}{' '}
					<Typography component="span" fontWeight="bold">
						{getIcon()} {level === 'FAN' ? 'ELITE' : 'VIP'}
					</Typography>
				</>
			</Typography>
		);
	};

	const handleOpenConfirm = () => {
		setOpenConfirm(true);
	};

	const handleCloseConfirm = () => {
		setOpenConfirm(false);
	};

	const handleCardUnlink = async (args: { email: string; password: string }) => {
		const response = await cardUnlink({
			...args,
			cardType: 2,
		});
		if (response) {
			onTabSelect(ACCOUNT_TABS.PERSONAL_INFORMATION);
			onMutateClubDpFlags();
			handleCloseConfirm();
			showErrorMessage('Tarjeta desvinculada correctamente');
		}
	};

	const [open, setOpen] = useState<boolean>(false);

	const handleToggle = () => {
		setOpen(!open);
	};

	if (loading) {
		return <MainCardViewSkeleton />;
	}

	if (!cards?.rewards) {
		return null;
	}

	return (
		<React.Fragment>
			<Typography component={'h1'} sx={ClubDpRewardsTitleSX}>
				{'TUS DATOS CLUB DPPUNTOS'}
			</Typography>
			<Stack direction={isMobile ? 'column' : 'row'} spacing={1} marginBottom={3}>
				<Typography sx={ClubDpRewardDescriptionSX}>
					{
						'Sigue acumulando puntos con tus compras para subir de nivel y obtén más beneficios exclusivos para ti.'
					}
				</Typography>
				<Linkable href="https://dpcardpuntos.com/" target="_blank">
					<Typography sx={ClubDpRewardsActionSX}>{'Conoce más'}</Typography>
				</Linkable>
			</Stack>

			<Stack direction={isMobile ? 'column' : 'row'} spacing={3}>
				<Stack spacing={2} display={'flex'} alignItems={'center'}>
					<Box sx={ClubDpRewardsCardFrameSX}>
						{!imageLoaded ? (
							<Skeleton
								sx={{ borderRadius: '4px' }}
								variant="rectangular"
								width={373}
								height={240}
							/>
						) : null}
						<Img
							src={getCardImage()}
							alt="Club_Rewards"
							sx={combineSX([ClubDpRewardsCardImgSX, { display: imageLoaded ? 'block' : 'none' }])}
							onLoad={() => setImageLoaded(true)}
						/>
						<Typography sx={ClubDpRewardsCardNumberSX}>{formatCardId(cardId)}</Typography>
						<Typography sx={ClubDpRewardsCardNameSX}>{customerName.toUpperCase()}</Typography>
						<Typography sx={ClubDpRewardsCardSinceSX}>{'MIEMBRO DESDE'}</Typography>
						<Typography sx={ClubDpRewardsCardDateSX}>{formattedDate}</Typography>
					</Box>
					{!isMobile ? (
						<ButtonBase
							component="a"
							disableRipple
							sx={ClubDpRewardsActionSX}
							onClick={handleOpenConfirm}
						>
							{'Desvincular tarjeta'}
						</ButtonBase>
					) : null}
				</Stack>
				<Stack direction="column" gap={1}>
					<Box sx={ClubDpRewardsCardHolderFrameSX(isMobile)}>
						<Stack direction="column" spacing={1}>
							<Typography component={'h2'} sx={ClubDpRewardsCardLabelSX}>
								{'Nombre del cliente'}
							</Typography>
							<Typography component={'h3'} sx={ClubDpRewardsCardHolderSX}>
								{customerName}
							</Typography>
						</Stack>
						<Linkable href="https://dpcardpuntos.com/" target="_blank">
							<Typography sx={ClubDpRewardsActionSX}>{'Actualizar'}</Typography>
						</Linkable>
					</Box>
					<Box sx={ClubDpRewardsCardPointsFrameSX(isMobile)}>
						<Typography component={'h2'} sx={ClubDpRewardsCardLabelSX}>
							{'Saldo en puntos rewards'}
						</Typography>
						<Typography component={'h3'} sx={ClubDpRewardsCardPointsSX}>
							<StarsIcon sx={ClubDpRewardsCardPointsIconSX} />
							{`${balancePoints} puntos`}
						</Typography>
						<Typography sx={ClubDpRewardsCardLabelBlackSX}>{getLevelMessage()}</Typography>
						<CustomProgressBar level={level} />
						<Button
							sx={ClubDpRewardsCollapseSX}
							onClick={handleToggle}
							endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
						>
							<Typography component={'h2'}>
								{'Estás en nivel '}
								<Typography variant="body1" component="span" fontWeight="bold">
									{level}
								</Typography>
								{' conoce los beneficios'}
							</Typography>
						</Button>
						<Collapse in={open}>
							<ContentRecommendation
								id={stripRewards}
								properties={{
									emsName: stripRewards,
								}}
							/>
						</Collapse>
					</Box>
					<Box sx={ClubDpRewardsCardPointsFrameSX(isMobile)}>
						<Stack
							direction={isMobile ? 'column' : 'row'}
							display={'flex'}
							alignItems={'center'}
							spacing={1}
							width={'100%'}
						>
							<Typography component={'h2'} sx={ClubDpRewardsCardHolderSX}>
								{'Conoce tu estado de cuenta aquí'}
							</Typography>
							<Button
								sx={ButtonRewardsSX}
								id="clubDpReward-suscribe-button"
								data-testid="clubDpReward-suscribe-button"
								variant="contained"
								onClick={() => setOpenModalPeriods(true)}
							>
								{'Estado de cuenta'}
							</Button>
						</Stack>
					</Box>
					<PeriodsModal
						periods={periods}
						balancePoints={balancePoints}
						open={openModalPeriods}
						onClose={() => setOpenModalPeriods(false)}
					/>
				</Stack>
				{isMobile ? (
					<ButtonBase
						component="a"
						disableRipple
						sx={ClubDpRewardsActionSX}
						onClick={handleOpenConfirm}
					>
						{'Desvincular tarjeta'}
					</ButtonBase>
				) : null}
			</Stack>
			<ConfirmCredentialsDialog
				open={openConfirm}
				title="Desvincular tarjeta Dp Rewards"
				description="Introduce tus credenciales para confirmar"
				onSubmit={handleCardUnlink}
				onClose={handleCloseConfirm}
			/>
			<Box sx={ClubDpRewardsDividerSX}>
				<Divider />
			</Box>
			<Grid item xs={12} md={6} sx={ClubDpRewardsTicketsTitleSX}>
				<ContentRecommendation
					id={emsNameRewards}
					properties={{
						emsName: emsNameRewards,
					}}
				/>
			</Grid>
			<Grid>
				<ContentRecommendation
					id={nextRewards}
					properties={{
						emsName: nextRewards,
					}}
				/>
			</Grid>
			<LatestMovementsTable latestMovements={latestMovements} />
		</React.Fragment>
	);
};
